// La forma de un teléfono, de un correo y del número de un documento (#155).
//
// Es la copia en TypeScript de `ContactFormats` y `DocumentMask` del dominio, y
// tiene que decir lo mismo: si una regla cambia allá, cambia acá. Las pruebas de
// `formatos.test.ts` usan los mismos ejemplos que las de C#.
//
// **El front guía, la API ordena.** Acá se pone el guion mientras se escribe y no
// se deja meter una letra donde va un dígito, pero quien acomoda el número en la
// máscara y lo rechaza si no cabe es la API: le llegan también la importación y la
// PWA. Por eso, si una máscara no se entiende, el campo deja escribir libre: un
// número sin formato es mejor que un formulario que no deja avanzar.
//
// Al final, la de un campo numérico (#156), que no tiene copia en el dominio: cada
// pantalla revisa sus rangos, y la API los suyos.

const DIGITO = '0';
const LETRA = 'A';
const CUALQUIERA = '*';
const SEPARADOR = '|';
/** `varchar(50)`, como en `DocumentMask.MaxLength`. */
const LARGO_MAXIMO_MASCARA = 50;

const esDigito = (c: string) => c >= '0' && c <= '9';
const esLetra = (c: string) => (c >= 'A' && c <= 'Z') || (c >= 'a' && c <= 'z');
const esEspacio = (c: string) => c === DIGITO || c === LETRA || c === CUALQUIERA;
const espacios = (forma: string) => [...forma].filter(esEspacio).length;

/** Los caracteres que un teléfono admite, además de los dígitos. */
const SIMBOLOS_TELEFONO = new Set([' ', '+', '(', ')', '-']);

/** Solo dígitos, espacios y `+ ( ) -`, con al menos un dígito. Mira el valor recortado. */
export function esTelefono(valor: string): boolean {
  const telefono = valor.trim();
  return [...telefono].some(esDigito) && [...telefono].every((c) => esDigito(c) || SIMBOLOS_TELEFONO.has(c));
}

/**
 * Lo que el teléfono muestra a cada tecla: una que no admite —una letra— no entra.
 * Lo que llega de golpe —algo pegado, un teléfono viejo con «ext. 5»— se deja como
 * está, por lo mismo que en `guiarDocumento`: quitarle las letras en silencio
 * guardaría otro número. El error lo dice el formulario al salir del campo.
 */
export function guiarTelefono(anterior: string, nuevo: string): string {
  if (nuevo.length !== anterior.length + 1) return nuevo;
  let i = 0;
  while (i < anterior.length && anterior[i] === nuevo[i]) i++;
  const tecla = nuevo[i] ?? '';
  return esDigito(tecla) || SIMBOLOS_TELEFONO.has(tecla) ? nuevo : anterior;
}

/** `algo@dominio.tld`, sin espacios y con una sola arroba. Mira el valor recortado. */
export function esCorreo(valor: string): boolean {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(valor.trim());
}

/**
 * Si la máscara se puede usar: cada forma lleva al menos un espacio y dos formas
 * no tienen la misma cantidad. La API no debería mandar otra cosa; si la manda,
 * el campo deja escribir libre.
 */
export function mascaraValida(mascara: string): boolean {
  if (mascara.length > LARGO_MAXIMO_MASCARA) return false;
  const formas = mascara.split(SEPARADOR);
  const cantidades = formas.map(espacios);
  // Un literal que es letra o dígito —`V-00000000`— haría que el número ya guardado
  // no volviera a caber: la `V` contaría como un carácter más.
  const literalesLimpios = formas.every((f) => [...f].every((c) => esEspacio(c) || !/[\p{L}\p{Nd}]/u.test(c)));
  return literalesLimpios && cantidades.every((n) => n > 0) && new Set(cantidades).size === cantidades.length;
}

/**
 * Las letras y los dígitos del número, en orden, o `null` si trae una letra que
 * no es ASCII —la eñe—: no cabe en ningún espacio, y descartarla sería otro número.
 */
function caracteres(numero: string): string[] | null {
  const todos = [...numero];
  if (todos.some((c) => /[\p{L}\p{N}]/u.test(c) && !esDigito(c) && !esLetra(c))) return null;
  return todos.filter((c) => esDigito(c) || esLetra(c));
}

const admite = (espacio: string, c: string) =>
  espacio === DIGITO ? esDigito(c) : espacio === LETRA ? esLetra(c) : true;

/**
 * Los caracteres puestos en la forma, hasta donde alcancen, o `null` si alguno no
 * va en su espacio. Un literal se pone solo si viene otro carácter después: así
 * borrar el último no deja el guion colgando.
 */
function poner(forma: string, cs: string[]): string | null {
  let resultado = '';
  let siguiente = 0;
  for (const espacio of forma) {
    if (siguiente === cs.length) break;
    if (!esEspacio(espacio)) {
      resultado += espacio;
      continue;
    }
    const c = cs[siguiente++] ?? '';
    if (!admite(espacio, c)) return null;
    resultado += c;
  }
  return resultado;
}

/**
 * El número acomodado en la forma que le corresponde por su cantidad de letras y
 * dígitos, o `null` si no cabe en ninguna. Sin máscara, tal cual. Es lo mismo que
 * hace la API al guardar (`DocumentMask.Arrange`).
 */
export function acomodarDocumento(mascara: string | null | undefined, numero: string): string | null {
  if (!mascara) return numero;
  const cs = caracteres(numero);
  if (cs === null) return null;
  const forma = mascara.split(SEPARADOR).find((f) => espacios(f) === cs.length);
  return forma === undefined ? null : poner(forma, cs);
}

/**
 * Lo que el campo muestra mientras se escribe: el número con los guiones puestos.
 *
 * Con varias formas, guía con la más corta en la que cabe lo escrito: con el NIT
 * (`0000-000000-000-0|00000000-0`), los primeros nueve dígitos salen como DUI y el
 * décimo pasa a la forma larga.
 *
 * Una tecla que no cabe —una letra donde va un dígito, un dígito de más— no
 * entra. Lo que llega de golpe y no cabe —algo pegado, un número viejo— se deja
 * como está: recortarlo en silencio sería guardar otro número, y el error lo dice
 * el formulario al salir del campo.
 */
export function guiarDocumento(mascara: string | null | undefined, anterior: string, nuevo: string): string {
  if (!mascara || !mascaraValida(mascara)) return nuevo;
  const cs = caracteres(nuevo);
  const formas = mascara
    .split(SEPARADOR)
    .map((forma) => ({ forma, n: espacios(forma) }))
    .sort((a, b) => a.n - b.n);

  if (cs !== null) {
    for (const { forma, n } of formas) {
      if (n < cs.length) continue;
      const guiado = poner(forma, cs);
      if (guiado !== null) return guiado;
    }
  }

  // Una sola tecla de más sobre algo que sí cabía: no entra.
  const tecla = nuevo.length === anterior.length + 1;
  return tecla && guiarDocumento(mascara, '', anterior) === anterior ? anterior : nuevo;
}

/** Si en la máscara solo van dígitos: el celular abre el teclado numérico. */
export function soloDigitos(mascara: string | null | undefined): boolean {
  return !!mascara && mascaraValida(mascara) && [...mascara].every((c) => !esEspacio(c) || c === DIGITO);
}

/** Lo que admite un campo numérico (#156). */
export interface FormaNumero {
  /** Cuántos decimales caben: `0` es un entero. Sin tope si no se dice. */
  decimales?: number | undefined;
  /** Cuántos dígitos caben delante del punto: `4` en un año. Sin tope si no se dice. */
  enteros?: number | undefined;
  /** Si deja escribir el `-` delante. Ningún campo lo usa hoy. */
  negativos?: boolean | undefined;
  /** Sin la coma de miles: un año es `2024`, no `2,024`. */
  sinMiles?: boolean | undefined;
}

/**
 * Si el texto es un número de esa forma, **también a medio escribir**: `''`, `'-'`,
 * `'12.'` y `'.5'` caben, porque son por donde se pasa al teclear. Que sea un número
 * entero de verdad lo dice el `validar` de la pantalla al guardar. Sin comas: es el
 * valor, no lo que se muestra.
 */
function cabeNumero({ decimales, enteros, negativos }: FormaNumero, texto: string): boolean {
  const signo = negativos ? '-?' : '';
  const entera = enteros === undefined ? '\\d*' : `\\d{0,${String(enteros)}}`;
  const fraccion = decimales === 0 ? '' : `(\\.\\d{0,${decimales === undefined ? '' : String(decimales)}})?`;
  return new RegExp(`^${signo}${entera}${fraccion}$`).test(texto);
}

/**
 * Como `cabeNumero`, con las comas que tecleó la persona todavía sin leer: van
 * solo delante del punto, y no en un campo `sinMiles`. Si eran de miles o el
 * decimal lo dice `resolverNumero` al salir del campo.
 */
function cabeEscrito(forma: FormaNumero, texto: string): boolean {
  if (!texto.includes(',')) return cabeNumero(forma, texto);
  const punto = texto.indexOf('.');
  const entera = punto === -1 ? texto : texto.slice(0, punto);
  const resto = texto.slice(entera.length);
  if (forma.sinMiles || resto.includes(',')) return false;
  return cabeNumero(forma, entera.replaceAll(',', '') + resto);
}

/**
 * Comas de miles bien puestas: `1,500`, `12,000.50`. El primer grupo no empieza
 * en 0: `0,750` es tres cuartos con coma decimal, no setecientos cincuenta.
 */
const COMAS_DE_MILES = /^-?[1-9]\d{0,2}(,\d{3})+(\.\d*)?$/;
/** Una sola coma con uno o dos dígitos detrás, sin punto: `45,50`, `1,5`. */
const COMA_DECIMAL = /^-?\d*,\d{1,2}$/;

/**
 * El valor de un número con las comas que tecleó o pegó la persona (#156), sin
 * ellas: si son grupos de tres, de miles —`1,500` → `1500`—; si es una sola con
 * uno o dos dígitos detrás, el decimal —`45,50` → `45.50`—. Cualquier otra cosa
 * —`1,5000`, `45,50.3`, `0,750`— es `null`: no se sabe qué quiso decir, y adivinar
 * guardaría otro número. El campo la marca y no deja guardar.
 */
export function resolverNumero(forma: FormaNumero, texto: string): string | null {
  if (!texto.includes(',')) return texto;
  const candidato = COMAS_DE_MILES.test(texto)
    ? texto.replaceAll(',', '')
    : forma.decimales !== 0 && COMA_DECIMAL.test(texto)
      ? texto.replace(',', '.')
      : null;
  return candidato !== null && cabeNumero(forma, candidato) ? candidato : null;
}

/**
 * Lo que la caja muestra de un valor numérico: con la coma de miles, que se pone
 * sola. `1500.00` → `1,500.00`. El valor se guarda sin ella, y por eso ninguna
 * pantalla tiene que quitarla para leerlo. Un valor que no cabe se muestra como
 * vino: ponerle comas sería cambiarlo. Tampoco a uno con una coma tecleada sin leer
 * todavía: mezclar las suyas con las del campo no dejaría saber cuál era cuál.
 */
export function mostrarNumero(forma: FormaNumero, valor: string): string {
  if (forma.sinMiles || !cabeNumero(forma, valor)) return valor;
  return valor.replace(
    /^(-?)(\d+)/,
    (_: string, signo: string, entera: string) => signo + entera.replace(/\B(?=(\d{3})+$)/g, ','),
  );
}

/**
 * El valor que deja un cambio en un campo numérico (#156): solo dígitos, comas y un
 * punto decimal, sin pasar del tope de decimales ni del de enteros. `anterior` es
 * el valor; `escrito`, lo que quedó en la caja, con las comas que mostraba.
 *
 * - Las comas que puso el campo no son del valor. La que se teclea, sí: queda como
 *   se escribió, y al salir del campo `resolverNumero` dice si era de miles o el
 *   decimal. En la tecla no se puede saber: `1,5` puede seguir a `1,500` o a `1,50`.
 * - Lo pegado (`pegado`, también lo arrastrado) entra entero o no entra: `$45` o
 *   `45 aprox` dejan el campo como estaba. Quitarle lo que sobra en silencio sería
 *   guardar otro número. Solo se le quitan los espacios de los bordes. Si trae
 *   comas, se leen ahí mismo, porque ya está completo: `1,500.00` entra como
 *   `1500.00` y `45,50` como `45.50`; `1,2,3` no entra.
 * - Lo que ya venía sin caber —un dato de antes de este campo— no traba el campo:
 *   se puede borrar y corregir, pero no meterle letras. El `validar` de la pantalla
 *   lo marca.
 */
export function guiarNumero(forma: FormaNumero, anterior: string, escrito: string, pegado = false): string {
  const mostrado = mostrarNumero(forma, anterior);
  const { antes, llego, despues } = partes(mostrado, escrito);
  const nuevo =
    mostrado === anterior ? escrito : antes.replaceAll(',', '') + llego + despues.replaceAll(',', '');

  if (pegado) {
    const todo = nuevo.trim();
    if (!todo.includes(',')) return cabeNumero(forma, todo) ? todo : anterior;
    return resolverNumero(forma, todo) ?? anterior;
  }

  if (cabeEscrito(forma, nuevo)) return nuevo;
  if (cabeEscrito(forma, anterior)) return anterior;
  const admitidos = new RegExp(`^[\\d.${forma.sinMiles ? '' : ','}${forma.negativos ? '-' : ''}]*$`);
  return admitidos.test(llego) ? escrito : anterior;
}

/**
 * `nuevo` partido en lo que no cambió al principio, lo que llegó y lo que no
 * cambió al final. Así se ve igual una tecla sola que una sobre una selección.
 */
function partes(anterior: string, nuevo: string): { antes: string; llego: string; despues: string } {
  let inicio = 0;
  while (inicio < anterior.length && inicio < nuevo.length && anterior[inicio] === nuevo[inicio]) inicio++;
  let fin = 0;
  while (
    fin < anterior.length - inicio &&
    fin < nuevo.length - inicio &&
    anterior[anterior.length - 1 - fin] === nuevo[nuevo.length - 1 - fin]
  ) {
    fin++;
  }
  return {
    antes: nuevo.slice(0, inicio),
    llego: nuevo.slice(inicio, nuevo.length - fin),
    despues: nuevo.slice(nuevo.length - fin),
  };
}
